import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';

export default function ProductoCuerpoPage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user } = useAuth();
  const [hasPurchased, setHasPurchased] = useState(false);
  const [loading, setLoading] = useState(true);

  // ---------- Scroll handling ----------
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on scroll
  useEffect(() => {
    if (menuOpen) setMenuOpen(false);
  }, [scrolled, menuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [menuOpen]);

  // ---------- Purchase check ----------
  useEffect(() => {
    const checkPurchase = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('user_purchases')
          .select('*')
          .eq('user_id', user.id)
          .in('product_slug', [
            'producto-cuerpo',
            'cuerpo',
            'despierta-tu-voz',
          ])
          .maybeSingle();

        if (error) {
          console.error('Error checking purchase:', error);
          setHasPurchased(false);
        } else if (data) {
          setHasPurchased(true);
        }
      } catch (e) {
        console.error('Unexpected error while checking purchase:', e);
        setHasPurchased(false);
      } finally {
        setLoading(false);
      }
    };

    checkPurchase();
  }, [user]);

  // ---------- Handlers ----------
  const handlePurchase = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const contactSection = document.getElementById('contacto');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // ---------- Navigation ----------
  const navLinks = [
    { href: '/#rituales', label: 'Rituales gratuitos' },
    { href: '/#metodo', label: 'Un método propio' },
    { href: '/#espacio', label: 'Despierta tu voz' },
    { href: '/#sobre-mi', label: 'La voz detrás' },
    { href: '/#contacto', label: 'Contacto' },
  ];

  const accentColor = '#f7cdce';
  const accentColorDark = '#e8b4b5';

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-md'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <Link
              to="/"
              className="flex items-center transform hover:scale-105 transition-transform duration-300"
            >
              <img
                src="https://static.readdy.ai/image/e4930f1328430401d33e8e8f8ac29155/d3c9f2d28e8d17a63b0e1d543ffef045.png"
                alt="Una voz más que propia"
                className="h-14 sm:h-16 w-auto"
              />
            </Link>

            {/* Desktop navigation */}
            <div className="hidden lg:flex items-center justify-center flex-1">
              <div className="flex items-center gap-8">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="text-sm font-medium transition-all duration-300 whitespace-nowrap hover:scale-110 cursor-pointer text-gray-700 hover:text-[#e8b4b5]"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Desktop login */}
            <div className="hidden lg:flex items-center">
              <Link
                to="/auth/login"
                className="px-5 py-2.5 bg-[#e8b4b5] text-white rounded-full text-sm font-semibold hover:bg-[#d9a3a4] transition-all duration-300 whitespace-nowrap shadow-md hover:shadow-lg flex items-center gap-2 transform hover:scale-105"
              >
                <i className="ri-user-line"></i>
                Mi espacio propio
              </Link>
            </div>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-300 text-gray-700 hover:bg-gray-100"
              aria-label="Abrir menú"
            >
              <i
                className={`${
                  menuOpen ? 'ri-close-line' : 'ri-menu-line'
                } text-2xl`}
              ></i>
            </button>
          </div>
        </div>

        {/* Mobile overlay */}
        <div
          className={`lg:hidden fixed inset-0 top-20 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
            menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
          onClick={() => setMenuOpen(false)}
        />

        {/* Mobile menu */}
        <div
          className={`lg:hidden fixed top-20 left-0 right-0 bg-white shadow-xl transition-all duration-300 transform ${
            menuOpen ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0 pointer-events-none'
          }`}
        >
          <div className="px-6 py-6 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMenuOpen(false)}
                className="block px-4 py-3 text-gray-700 hover:text-[#e8b4b5] hover:bg-[#f7cdce]/10 rounded-lg transition-all duration-200 font-medium"
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/auth/login"
              className="flex items-center justify-center gap-2 px-5 py-3 bg-[#e8b4b5] text-white rounded-full font-semibold hover:bg-[#d9a3a4] transition-all duration-300 shadow-md hover:shadow-lg mt-4"
            >
              <i className="ri-user-line"></i>
              Mi espacio propio
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-b from-[#fdf5f5] to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#f7cdce]/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#f7cdce]/10 rounded-full blur-3xl"></div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left – Content */}
            <div>
              <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-[#f7cdce]/20 rounded-full">
                <span className="w-2 h-2 bg-[#e8b4b5] rounded-full animate-pulse"></span>
                <span className="text-[#d9a3a4] text-sm font-semibold">
                  ETAPA 2 • RITUAL CUERPO
                </span>
              </div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif text-gray-900 mb-6 leading-tight">
                Cuerpo
              </h1>

              <p className="text-xl md:text-2xl text-gray-700 mb-8 leading-relaxed">
                Un <strong>escaneo corporal atípico</strong> que traduce sensaciones en palabras. 
                Sinestesia narrativa, cartografía emocional y narratología corporal (lo sé... vaya palabrita...) para habitar 
                tu voz desde la piel.
              </p>

              <div className="inline-block mb-10 px-6 py-3 bg-white rounded-2xl shadow-md border border-gray-100">
                <p className="text-lg text-gray-600 italic">
                  Deja de pensar qué escribir.{' '}
                  <strong className="text-[#e8b4b5] not-italic">Siente qué escribir.</strong>
                </p>
              </div>

              {/* Trust badges */}
              <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <i className="ri-music-2-line text-[#e8b4b5] text-xl"></i>
                  <span>963Hz + 741Hz</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-refresh-line text-[#e8b4b5] text-xl"></i>
                  <span>Repite cuando quieras</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-download-line text-[#e8b4b5] text-xl"></i>
                  <span>Materiales descargables</span>
                </div>
              </div>
            </div>

            {/* Right – Image */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#f7cdce]/30 to-transparent rounded-3xl blur-2xl"></div>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://static.readdy.ai/image/e4930f1328430401d33e8e8f8ac29155/6e2c642cf8799ae8203393e40b1b7dd2.png"
                  alt="Ritual Cuerpo"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Literary Background Section */}
      <section className="py-16 px-6 bg-gradient-to-r from-[#f7cdce]/10 via-white to-[#f7cdce]/10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-[#d9a3a4] text-sm font-semibold uppercase tracking-wider">
              Trasfondo literario
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mt-2">
              Tres disciplinas que convergen en tu escritura
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#f7cdce]/30 hover:shadow-xl transition-all">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#f7cdce] to-[#e8b4b5] rounded-xl mb-5">
                <i className="ri-palette-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-serif text-gray-900 mb-3">Sinestesia narrativa</h3>
              <p className="text-gray-600 leading-relaxed">
                Cruzar los sentidos en la escritura: ver sonidos, escuchar colores, tocar emociones. 
                Tu cuerpo percibe en capas que la mente no separa.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#f7cdce]/30 hover:shadow-xl transition-all">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#f7cdce] to-[#e8b4b5] rounded-xl mb-5">
                <i className="ri-map-pin-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-serif text-gray-900 mb-3">Cartografía emocional</h3>
              <p className="text-gray-600 leading-relaxed">
                Mapear el territorio interno: dónde vive cada emoción, qué forma tiene, 
                qué caminos conectan una sensación con otra.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#f7cdce]/30 hover:shadow-xl transition-all">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#f7cdce] to-[#e8b4b5] rounded-xl mb-5">
                <i className="ri-body-scan-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-serif text-gray-900 mb-3">Narratología corporal</h3>
              <p className="text-gray-600 leading-relaxed">
                El cuerpo como texto: cada tensión cuenta una historia, cada gesto es sintaxis, 
                cada respiración un punto y aparte.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included – 3 Phases Method */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-[#f7cdce]/20 rounded-full">
              <i className="ri-award-line text-[#e8b4b5]"></i>
              <span className="text-[#d9a3a4] text-sm font-semibold">
                MÉTODO COMPLETO EN 3 FASES
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4">
              Un viaje hacia tu cuerpo creativo
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Un <strong>sistema completo de 3 fases</strong> que conecta tus sensaciones físicas
              con tu escritura a través de sinestesia, cartografía y narratología corporal.
            </p>
          </div>

          {/* Phase 1 */}
          <div className="mb-12">
            <div className="bg-gradient-to-br from-[#fdf5f5] to-white rounded-3xl border border-gray-100 overflow-hidden shadow-lg hover:shadow-xl transition-all">
              <div className="grid lg:grid-cols-2 gap-0">
                <div className="p-10 lg:p-12">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#f7cdce] to-[#e8b4b5] rounded-2xl text-white font-bold text-xl shadow-lg">
                      1
                    </div>
                    <div>
                      <span className="text-xs text-[#d9a3a4] font-semibold uppercase tracking-wider">
                        Fase 1
                      </span>
                      <h3 className="text-2xl md:text-3xl font-serif text-gray-900">
                        Tomar conciencia
                      </h3>
                    </div>
                  </div>

                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    Un <strong>escaneo corporal atípico</strong> que no busca relajar, sino despertar. 
                    Aquí empiezas a notar lo que normalmente ignoras.
                  </p>

                  <div className="space-y-4">
                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#f7cdce]/30 rounded-lg flex-shrink-0">
                        <i className="ri-bookmark-line text-[#e8b4b5] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">
                          Tarjetas de intención sinestésica
                        </h4>
                        <p className="text-sm text-gray-600">
                          Elige desde qué sentido vas a entrar: el canal que elijas cambia la experiencia
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#f7cdce]/30 rounded-lg flex-shrink-0">
                        <i className="ri-headphone-line text-[#e8b4b5] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">
                          Audio guiado — Escaneo corporal atípico
                        </h4>
                        <p className="text-sm text-gray-600">
                          Respiración + escaneo por zonas + atención a micro-sensaciones cruzadas
                        </p>
                        <span className="inline-flex items-center gap-1 mt-2 text-xs text-[#d9a3a4] font-medium">
                          <i className="ri-music-2-line"></i> Frecuencias 963Hz + 741Hz
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="relative h-64 lg:h-auto">
                  <img
                    src="https://static.readdy.ai/image/e4930f1328430401d33e8e8f8ac29155/25dd41748e6ae75fa7b35440dcb43154.png"
                    alt="Fase 1: Tomar conciencia corporal"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#fdf5f5] via-transparent to-transparent lg:from-transparent"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Phase 2 */}
          <div className="mb-12">
            <div className="bg-gradient-to-br from-[#fdf5f5] to-white rounded-3xl border border-gray-100 overflow-hidden shadow-lg hover:shadow-xl transition-all">
              <div className="grid lg:grid-cols-2 gap-0">
                <div className="relative h-64 lg:h-auto order-2 lg:order-1">
                  <img
                    src="https://public.readdy.ai/ai/img_res/921553a522937c0ce5febf2428d0201d.jpg"
                    alt="Fase 2: Cartografía emocional"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-l from-[#fdf5f5] via-transparent to-transparent lg:from-transparent"></div>
                </div>

                <div className="p-10 lg:p-12 order-1 lg:order-2">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#f7cdce] to-[#e8b4b5] rounded-2xl text-white font-bold text-xl shadow-lg">
                      2
                    </div>
                    <div>
                      <span className="text-xs text-[#d9a3a4] font-semibold uppercase tracking-wider">
                        Fase 2
                      </span>
                      <h3 className="text-2xl md:text-3xl font-serif text-gray-900">
                        Dejarse llevar
                      </h3>
                    </div>
                  </div>

                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    Cartografía emocional: traducimos el cuerpo a escritura <strong>sin pasar por el filtro</strong> de
                    "qué significa". Aquí recoges sensaciones puras y las cruzas.
                  </p>

                  <div className="space-y-4">
                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#f7cdce]/30 rounded-lg flex-shrink-0">
                        <i className="ri-map-2-line text-[#e8b4b5] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Mapa de Cartografía Emocional</h4>
                        <p className="text-sm text-gray-600">
                          Temperatura, textura, color, sonido, sabor... sensaciones cruzadas convertidas en material creativo
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#f7cdce]/30 rounded-lg flex-shrink-0">
                        <i className="ri-volume-up-line text-[#e8b4b5] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Mini audio guía sinestésica</h4>
                        <p className="text-sm text-gray-600">
                          Te acompaña mientras completas el mapa, cruzando sentidos para que la mente no te lleve lejos
                        </p>
                        <span className="inline-flex items-center gap-1 mt-2 text-xs text-[#d9a3a4] font-medium">
                          <i className="ri-file-download-line"></i> Descargable en PDF
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Phase 3 */}
          <div className="mb-12">
            <div className="bg-gradient-to-br from-[#fdf5f5] to-white rounded-3xl border border-gray-100 overflow-hidden shadow-lg hover:shadow-xl transition-all">
              <div className="grid lg:grid-cols-2 gap-0">
                <div className="p-10 lg:p-12">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#f7cdce] to-[#e8b4b5] rounded-2xl text-white font-bold text-xl shadow-lg">
                      3
                    </div>
                    <div>
                      <span className="text-xs text-[#d9a3a4] font-semibold uppercase tracking-wider">
                        Fase 3
                      </span>
                      <h3 className="text-2xl md:text-3xl font-serif text-gray-900">
                        Integrarlo
                      </h3>
                    </div>
                  </div>

                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    Narratología corporal: lo que notas hoy se convierte en{' '}
                    <strong>lenguaje que puedas sostener</strong>. El cuerpo como texto, cada gesto como sintaxis.
                  </p>

                  <div className="space-y-4">
                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#f7cdce]/30 rounded-lg flex-shrink-0">
                        <i className="ri-book-3-line text-[#e8b4b5] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Diario de narratología corporal</h4>
                        <p className="text-sm text-gray-600">
                          Identifica patrones ("mi cuerpo narra así") y quédate con lo que tiene verdad
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#f7cdce]/30 rounded-lg flex-shrink-0">
                        <i className="ri-edit-line text-[#e8b4b5] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">
                          Ejercicios de sinestesia narrativa
                        </h4>
                        <p className="text-sm text-gray-600">
                          Cruza sentidos en tu escritura: ver sonidos, escuchar colores, tocar emociones
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#f7cdce]/30 rounded-lg flex-shrink-0">
                        <i className="ri-heart-line text-[#e8b4b5] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Cierre de encarnación</h4>
                        <p className="text-sm text-gray-600">
                          Un gesto pequeño que aterrice lo descubierto: un límite, una decisión, una
                          frase-regla
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="relative h-64 lg:h-auto">
                  <img
                    src="https://readdy.ai/api/search-image?query=Empowered%20woman%20writer%20at%20desk%20with%20journal%20writing%20from%20body%20sensations%20soft%20pink%20cotton%20light%20emanating%20from%20heart%20and%20hands%20representing%20embodied%20writing%20and%20somatic%20creativity%20warm%20rose%20muted%20tones%20artistic%20inspirational%20style&width=600&height=700&seq=cuerpo-phase3-rosa-v1&orientation=portrait"
                    alt="Fase 3: Integrar en escritura"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#fdf5f5] via-transparent to-transparent lg:from-transparent"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary Value Box */}
          <div className="bg-gradient-to-r from-[#f7cdce]/20 via-[#f7cdce]/10 to-[#f7cdce]/20 rounded-3xl p-8 md:p-10 border border-[#f7cdce]/30">
            <div className="text-center mb-8">
              <h3 className="text-2xl md:text-3xl font-serif text-gray-900 mb-2">
                Todo esto incluido por solo <span className="text-[#d9a3a4]">64€</span>
              </h3>
              <p className="text-gray-600">Pago único • Acceso inmediato • Tuyo para siempre</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-xl p-5 shadow-sm border border-[#f7cdce]/20">
                <div className="w-12 h-12 flex items-center justify-center bg-[#f7cdce]/30 rounded-full mb-3">
                  <i className="ri-headphone-line text-[#e8b4b5] text-2xl"></i>
                </div>
                <p className="text-base font-semibold text-gray-900 mb-1">
                  Escaneo Corporal Atípico
                </p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Audio guiado a 963Hz + 741Hz para despertar sensaciones cruzadas
                </p>
              </div>

              <div className="bg-white rounded-xl p-5 shadow-sm border border-[#f7cdce]/20">
                <div className="w-12 h-12 flex items-center justify-center bg-[#f7cdce]/30 rounded-full mb-3">
                  <i className="ri-map-2-line text-[#e8b4b5] text-2xl"></i>
                </div>
                <p className="text-base font-semibold text-gray-900 mb-1">Cartografía Emocional</p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Mapa único para traducir el cuerpo a escritura sinestésica
                </p>
              </div>

              <div className="bg-white rounded-xl p-5 shadow-sm border border-[#f7cdce]/20">
                <div className="w-12 h-12 flex items-center justify-center bg-[#f7cdce]/30 rounded-full mb-3">
                  <i className="ri-book-3-line text-[#e8b4b5] text-2xl"></i>
                </div>
                <p className="text-base font-semibold text-gray-900 mb-1">Diario de Narratología</p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Guía para leer tu cuerpo como texto y encontrar tu sintaxis
                </p>
              </div>

              <div className="bg-white rounded-xl p-5 shadow-sm border border-[#f7cdce]/20">
                <div className="w-12 h-12 flex items-center justify-center bg-[#f7cdce]/30 rounded-full mb-3">
                  <i className="ri-palette-line text-[#e8b4b5] text-2xl"></i>
                </div>
                <p className="text-base font-semibold text-gray-900 mb-1">Ejercicios Sinestésicos</p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Banco de ejercicios para cruzar sentidos en tu escritura
                </p>
              </div>
            </div>

            <div className="text-center">
              {loading ? (
                <div className="px-10 py-4 bg-gray-200 rounded-full text-gray-500 font-semibold inline-block">
                  Cargando...
                </div>
              ) : hasPurchased ? (
                <Link
                  to="/contenido/cuerpo"
                  className="group inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-[#f7cdce] to-[#e8b4b5] text-white rounded-full text-lg font-semibold hover:shadow-xl transition-all whitespace-nowrap transform hover:scale-105"
                >
                  <span>Acceder al contenido</span>
                  <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
                </Link>
              ) : (
                <a
                  href="#contacto"
                  onClick={handlePurchase}
                  className="group inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-[#f7cdce] to-[#e8b4b5] text-white rounded-full text-lg font-semibold hover:shadow-xl transition-all whitespace-nowrap transform hover:scale-105"
                >
                  <span>Quiero empezar mi ritual Cuerpo</span>
                  <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-6 bg-[#fdf5f5]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-serif text-gray-900 mb-4">
              Por qué funciona
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#f7cdce] to-[#e8b4b5] mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="group bg-white rounded-3xl p-10 shadow-lg border border-gray-100 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#f7cdce]/40 to-[#f7cdce]/20 rounded-2xl mb-8 group-hover:scale-110 transition-transform duration-500">
                <i className="ri-palette-line text-4xl text-[#d9a3a4]"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-serif text-gray-900 mb-4 leading-tight">
                Sinestesia que <span className="text-[#d9a3a4]">despierta</span>
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Cruzar los sentidos abre puertas que la percepción lineal mantiene cerradas. 
                <strong className="text-gray-800"> Ver sonidos, escuchar colores</strong>: tu escritura se vuelve multidimensional.
              </p>
            </div>

            <div className="group bg-white rounded-3xl p-10 shadow-lg border border-gray-100 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#f7cdce]/40 to-[#f7cdce]/20 rounded-2xl mb-8 group-hover:scale-110 transition-transform duration-500">
                <i className="ri-map-pin-line text-4xl text-[#d9a3a4]"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-serif text-gray-900 mb-4 leading-tight">
                Cartografía que <span className="text-[#d9a3a4]">orienta</span>
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Mapear el territorio interno te da un lenguaje para lo que antes era confuso. 
                <strong className="text-gray-800"> Sabrás dónde vive cada emoción</strong> y cómo llegar a ella cuando escribas.
              </p>
            </div>

            <div className="group bg-white rounded-3xl p-10 shadow-lg border border-gray-100 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#f7cdce]/40 to-[#f7cdce]/20 rounded-2xl mb-8 group-hover:scale-110 transition-transform duration-500">
                <i className="ri-body-scan-line text-4xl text-[#d9a3a4]"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-serif text-gray-900 mb-4 leading-tight">
                Narratología que <span className="text-[#d9a3a4]">traduce</span>
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Tu cuerpo ya cuenta historias. Cada tensión es un párrafo, cada gesto una frase. 
                <strong className="text-gray-800"> Aprende a leer tu propia sintaxis corporal</strong>.
              </p>
            </div>

            <div className="group bg-white rounded-3xl p-10 shadow-lg border border-gray-100 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#f7cdce]/40 to-[#f7cdce]/20 rounded-2xl mb-8 group-hover:scale-110 transition-transform duration-500">
                <i className="ri-music-2-line text-4xl text-[#d9a3a4]"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-serif text-gray-900 mb-4 leading-tight">
                Frecuencias que <span className="text-[#d9a3a4]">afinan</span>
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                963Hz (frecuencia de la glándula pineal) + 741Hz (expresión y soluciones). 
                <strong className="text-gray-800"> Una combinación que abre canales de percepción</strong> y facilita la expresión.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Preview Section */}
      <section className="py-20 px-6 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-72 h-72 bg-[#f7cdce]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-72 h-72 bg-[#f7cdce]/10 rounded-full blur-3xl"></div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-[#f7cdce]/20 rounded-full">
              <i className="ri-eye-line text-[#e8b4b5]"></i>
              <span className="text-[#d9a3a4] text-sm font-semibold">
                DESCUBRE LO QUE INCLUYE
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4">
              Un vistazo al ritual
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Escucha un fragmento del escaneo corporal atípico y conoce los materiales sinestésicos
            </p>
          </div>

          {/* Audio Preview */}
          <div className="max-w-2xl mx-auto mb-12">
            <div className="bg-gradient-to-br from-[#fdf5f5] to-white rounded-3xl p-8 border border-gray-100 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-[#f7cdce] to-[#e8b4b5] rounded-xl shadow-md">
                  <i className="ri-headphone-line text-white text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-serif text-gray-900">
                    Escaneo corporal atípico
                  </h3>
                  <p className="text-sm text-gray-500">Fragmento de 20 segundos</p>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm mb-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#f7cdce]/30 to-[#f7cdce]/10 rounded-full">
                    <i className="ri-music-2-line text-[#d9a3a4] text-3xl"></i>
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 mb-1">
                      Ritual Cuerpo — 963Hz + 741Hz
                    </p>
                    <p className="text-sm text-gray-500">Escaneo sinestésico guiado</p>
                  </div>
                </div>

                <audio controls className="w-full h-12 rounded-lg" style={{ accentColor: '#e8b4b5' }}>
                  <source src="https://static.readdy.ai/audio/body-scan-preview-20s.mp3" type="audio/mpeg" />
                  Tu navegador no soporta el elemento de audio.
                </audio>

                <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-timer-line"></i> Vista previa: 0:20
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i> Duración completa: 25 min
                  </span>
                </div>
              </div>

              <p className="text-sm text-gray-600 italic text-center">
                "Nota el color de la tensión en tus hombros... ¿qué sonido haría si pudiera hablar?..."
              </p>
            </div>
          </div>

          {/* Materials Preview */}
          <div className="grid md:grid-cols-3 gap-6">
            {/* Card 1 */}
            <div className="group bg-gradient-to-br from-[#fdf5f5] to-white rounded-3xl p-6 border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-500">
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-5">
                  <div className="w-40 h-52 rounded-xl overflow-hidden shadow-lg transform group-hover:scale-105 transition-transform duration-500">
                    <img
                      src="https://readdy.ai/api/search-image?query=Elegant%20intention%20cards%20deck%20with%20soft%20pink%20cotton%20edges%20and%20synesthesia%20illustrations%20on%20cream%20paper%20featuring%20crossed%20senses%20symbols%20and%20sensory%20flow%20minimalist%20artistic%20design%20warm%20rose%20muted%20tones%20product%20photography%20on%20marble%20surface&width=300&height=400&seq=tarjetas-cuerpo-rosa-v1&orientation=portrait"
                      alt="Tarjetas de intención sinestésica"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-10 h-10 flex items-center justify-center bg-[#e8b4b5] rounded-full shadow-md">
                    <i className="ri-bookmark-line text-white text-lg"></i>
                  </div>
                </div>
                <h3 className="text-xl font-serif text-gray-900 mb-2">Tarjetas sinestésicas</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  Elige desde qué sentido vas a entrar: el canal que elijas cambia la experiencia.
                </p>
                <span className="inline-flex items-center gap-1 text-xs text-[#d9a3a4] font-medium bg-[#f7cdce]/20 px-3 py-1 rounded-full">
                  <i className="ri-file-download-line"></i> PDF descargable
                </span>
              </div>
            </div>

            {/* Card 2 */}
            <div className="group bg-gradient-to-br from-[#fdf5f5] to-white rounded-3xl p-6 border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-500">
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-5">
                  <div className="w-40 h-52 rounded-xl overflow-hidden shadow-lg transform group-hover:scale-105 transition-transform duration-500">
                    <img
                      src="https://readdy.ai/api/search-image?query=Beautiful%20emotional%20cartography%20map%20poster%20with%20human%20silhouette%20and%20flowing%20colored%20energy%20lines%20zones%20for%20emotions%20sensations%20on%20aged%20parchment%20paper%20soft%20pink%20cotton%20rose%20tones%20artistic%20stationery%20product%20photography%20on%20wooden%20desk&width=300&height=400&seq=mapa-cartografia-rosa-v1&orientation=portrait"
                      alt="Mapa de cartografía emocional"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-10 h-10 flex items-center justify-center bg-[#e8b4b5] rounded-full shadow-md">
                    <i className="ri-map-2-line text-white text-lg"></i>
                  </div>
                </div>
                <h3 className="text-xl font-serif text-gray-900 mb-2">Cartografía emocional</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  Mapea tu territorio interno: dónde vive cada emoción, qué forma tiene, qué caminos la conectan.
                </p>
                <span className="inline-flex items-center gap-1 text-xs text-[#d9a3a4] font-medium bg-[#f7cdce]/20 px-3 py-1 rounded-full">
                  <i className="ri-palette-line"></i> Ilustrado a mano
                </span>
              </div>
            </div>

            {/* Card 3 */}
            <div className="group bg-gradient-to-br from-[#fdf5f5] to-white rounded-3xl p-6 border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-500">
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-5">
                  <div className="w-40 h-52 rounded-xl overflow-hidden shadow-lg transform group-hover:scale-105 transition-transform duration-500">
                    <img
                      src="https://readdy.ai/api/search-image?query=Beautiful%20narratology%20journal%20with%20soft%20pink%20cotton%20cover%20and%20body%20as%20text%20illustrations%20handwritten%20calligraphy%20title%20elegant%20minimalist%20design%20warm%20rose%20muted%20tones%20product%20photography%20on%20wooden%20desk%20with%20dried%20flowers&width=300&height=400&seq=diario-narratologia-rosa-v1&orientation=portrait"
                      alt="Diario de narratología corporal"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-10 h-10 flex items-center justify-center bg-[#e8b4b5] rounded-full shadow-md">
                    <i className="ri-book-3-line text-white text-lg"></i>
                  </div>
                </div>
                <h3 className="text-xl font-serif text-gray-900 mb-2">Diario de narratología</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  Lee tu cuerpo como texto: cada tensión es un párrafo, cada gesto una frase.
                </p>
                <span className="inline-flex items-center gap-1 text-xs text-[#d9a3a4] font-medium bg-[#f7cdce]/20 px-3 py-1 rounded-full">
                  <i className="ri-file-download-line"></i> +20 páginas guiadas
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="contacto" className="py-24 px-6 bg-gradient-to-b from-white to-[#fdf5f5]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-6">
            Empieza tu ritual Cuerpo hoy
          </h2>
          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
            Sinestesia narrativa, cartografía emocional y narratología corporal para habitar tu voz desde la piel
          </p>

          <div className="bg-white rounded-3xl p-10 shadow-xl border border-gray-100 mb-10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="text-left">
                <div className="text-5xl font-bold text-[#e8b4b5] mb-2">64€</div>
                <p className="text-gray-600">Pago único • Acceso inmediato</p>
                <p className="text-sm text-gray-500 mt-2">Acceso ilimitado para siempre</p>
              </div>

              {loading ? (
                <div className="px-10 py-4 bg-gray-200 rounded-full text-gray-500 font-semibold">
                  Cargando...
                </div>
              ) : hasPurchased ? (
                <Link
                  to="/contenido/cuerpo"
                  className="group inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-[#f7cdce] to-[#e8b4b5] text-white rounded-full text-lg font-semibold hover:shadow-xl transition-all whitespace-nowrap transform hover:scale-105"
                >
                  <span>Acceder al contenido</span>
                  <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
                </Link>
              ) : (
                <a
                  href="mailto:contacto@unavozmasquepropia.com?subject=Quiero%20comprar%20Ritual%20Cuerpo&body=Hola,%20estoy%20interesada%20en%20comprar%20el%20Ritual%20Cuerpo%20por%2064€."
                  className="group inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-[#f7cdce] to-[#e8b4b5] text-white rounded-full text-lg font-semibold hover:shadow-xl transition-all whitespace-nowrap transform hover:scale-105"
                >
                  <span>Empezar mi ritual Cuerpo</span>
                  <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
                </a>
              )}
            </div>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-6 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <i className="ri-shield-check-line text-[#e8b4b5] text-xl"></i>
              <span>Pago seguro</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-customer-service-line text-[#e8b4b5] text-xl"></i>
              <span>Soporte personalizado</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-heart-line text-[#e8b4b5] text-xl"></i>
              <span>Hecho con amor</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#4a3f3f] text-white py-12 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <Link to="/" className="inline-block mb-6">
            <img
              src="https://static.readdy.ai/image/e4930f1328430401d33e8e8f8ac29155/d3c9f2d28e8d17a63b0e1d543ffef045.png"
              alt="Una voz más que propia"
              className="h-16 w-auto mx-auto"
            />
          </Link>
          <p className="text-white/70 mb-6">
            Encuentra tu voz única a través de la meditación y la escritura creativa
          </p>
          <div className="flex justify-center gap-6 mb-6">
            <a
              href="mailto:contacto@unavozmasquepropia.com"
              className="text-white/70 hover:text-white transition-colors"
            >
              <i className="ri-mail-line text-2xl"></i>
            </a>
          </div>
          <div className="border-t border-white/10 pt-6">
            <p className="text-white/50 text-sm">
              © 2026 Una voz más que propia. Todos los derechos reservados. •{' '}
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors"
              >
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
